import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import api from '@/lib/api';

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, accessToken, clearAuth, setAuth, _hasHydrated } = useAuthStore();
  const [verified, setVerified] = useState<'pending' | 'ok' | 'failed'>('pending');

  useEffect(() => {
    if (!_hasHydrated) return;

    if (!accessToken) {
      // No token in this tab — try a silent refresh. Covers landing on
      // /dashboard straight from the Google OAuth callback (which only
      // sets the refresh cookie) and reopening the app in a new tab.
      api.post('/api/auth/refresh')
        .then(({ data }) => {
          setAuth(data.accessToken, data.user);
          setVerified('ok');
        })
        .catch(() => setVerified('failed'));
      return;
    }

    // Verify the restored token is still valid
    api.get('/api/auth/me')
      .then(({ data }) => {
        setAuth(accessToken, data.user);
        setVerified('ok');
      })
      .catch(() => {
        clearAuth();
        setVerified('failed');
      });
  }, [_hasHydrated, accessToken]);

  // Wait for store hydration and the token check / silent refresh
  if (!_hasHydrated || verified === 'pending') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0a0a0b]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gold border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated || verified === 'failed') {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/** Redirect users who lack the required role to their default page */
export function RequireRole({ roles, children }: { roles: string[]; children: React.ReactNode }) {
  const role = useAuthStore((s) => s.user?.role);

  // SuperAdmin has full access — same as Owner for all business routes
  if (role === 'SuperAdmin' || (role && roles.includes(role))) {
    return <>{children}</>;
  }

  const dest = getDefaultRoute(role);
  return <Navigate to={dest} replace />;
}

export function getDefaultRoute(role: string | undefined): string {
  switch (role) {
    case 'Polisher':
      return '/worker';
    case 'Sawer':
      return '/dashboard/sawing';
    case 'QCInspector':
      return '/dashboard/stone';
    case 'Owner':
    case 'OperationsManager':
    case 'SuperAdmin':
    default:
      return '/dashboard';
  }
}
